import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, createMasonryCellPositioner, Masonry } from 'react-virtualized';
import { Button } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import usePreviousValue from 'packages/hooks/usePrevious';
import { Box, Cell, LoadingLayer } from './Grid.styled';
import { GridProps } from './types';
import { parseAspectRatio } from '../card/utils';
import { calculateColumnCount } from './utils';

const GRID_COLUMN_WIDTH = 224;
const GRID_GUTTER_SIZE = 24;
const DEFAULT_CARD_HEIGHT = 214;
const COMMON_WIDTH = 1062; // based on 1366 screen width
const NUMBER_OF_CARDS_TO_PRERENDER = 3;

function Grid<T>({
    items = [],
    renderItem,
    compareItems,
    isLoading = false,
    columnWidth = GRID_COLUMN_WIDTH,
    gutterSize = GRID_GUTTER_SIZE,
    defaultCardHeight = DEFAULT_CARD_HEIGHT,
    onScroll,
    overscanByPixels = (defaultCardHeight + gutterSize) * NUMBER_OF_CARDS_TO_PRERENDER,
    scrollThreshold = defaultCardHeight + gutterSize,
}: GridProps<T>) {
    const { translate } = useTranslate();
    const masonryGridRef = useRef<typeof Masonry | undefined>(undefined);
    const box = useRef<HTMLDivElement>(null);
    const [boxHeight, setBoxHeight] = useState(0);

    useEffect(() => {
        if (!box.current) {
            return;
        }

        setBoxHeight(box.current.clientHeight);

        // Box is `height: 100%`, so its height changes on layout alone — a
        // MutationObserver misses that and only fired here by accident, when
        // cells were added as children.
        const observer = new ResizeObserver(() => {
            if (box.current) {
                setBoxHeight(box.current.clientHeight);
            }
        });

        observer.observe(box.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    const measurerCache = useMemo(
        () =>
            new CellMeasurerCache({
                defaultHeight: defaultCardHeight,
                defaultWidth: columnWidth,
                fixedWidth: true,
            }),
        [],
    );

    const cellPositioner = useCallback(
        createMasonryCellPositioner({
            cellMeasurerCache: measurerCache,
            columnCount: calculateColumnCount(COMMON_WIDTH, columnWidth, gutterSize),
            columnWidth,
            spacer: gutterSize,
        }),
        [],
    );

    const resetCells = (width: number) => {
        cellPositioner.reset({
            cellMeasurerCache: measurerCache,
            columnCount: calculateColumnCount(width, columnWidth, gutterSize),
            columnWidth,
            spacer: gutterSize,
        });

        masonryGridRef.current?.recomputeCellPositions();
    };

    const clearCells = (width: number) => {
        measurerCache.clearAll();
        cellPositioner.reset({
            cellMeasurerCache: measurerCache,
            columnCount: calculateColumnCount(width, columnWidth, gutterSize),
            columnWidth,
            spacer: gutterSize,
        });

        masonryGridRef.current.clearCellPositions();
    };

    const prevItems = usePreviousValue(items);

    const checkDiff = (prev, next) => {
        if (!prev?.length) {
            return false;
        }

        const order = prev.length < next.length ? [prev, next] : [next, prev];

        return order[0].every((item, index) => compareItems(item, order[1][index]));
    };

    useEffect(() => {
        // it will skip first load but reset cells on each new page loaded
        // onGridResize will handle first load
        if (masonryGridRef.current && masonryGridRef.current.props.width) {
            if (checkDiff(prevItems, items)) {
                resetCells(masonryGridRef.current.props.width);
            } else {
                clearCells(masonryGridRef.current.props.width);

                masonryGridRef.current._scrollingContainer.scrollTop = 0;
            }
        }
    }, [items]);

    // in case if loaded, for example, 20 items as first page
    // but that is not enough to fill application page and free space left
    // then x function will call next page until application page is full
    const onCellsRendered = () => {
        if (masonryGridRef.current) {
            const { scrollHeight } = masonryGridRef.current._scrollingContainer;

            if (boxHeight !== 0 && scrollHeight !== 0 && boxHeight >= scrollHeight) {
                onScroll();
            }
        }
    };

    useEffect(() => onCellsRendered(), [boxHeight]);

    const onGridResize = ({ width }) => {
        resetCells(width);
    };

    const handleOnScroll = ({ clientHeight, scrollHeight, scrollTop }) => {
        if (clientHeight + scrollTop >= scrollHeight - scrollThreshold) {
            onScroll();
        }
    };

    const renderGridItem = ({ index, key, parent, style }) => {
        // @ts-ignore
        const item: Record<string, unknown> & { width?: number; height?: number; aspectRatio?: string } = items[index];
        let aspectRatio;

        if (
            item &&
            !('aspectRatio' in item) &&
            'height' in item &&
            'width' in item &&
            item.height !== 0 &&
            item.width !== 0
        ) {
            aspectRatio = item.width / item.height;
        } else {
            aspectRatio = parseAspectRatio(item && item.aspectRatio);
        }

        // columnWidth calculated with 4 * 2 padding for outline
        // but outline space not available for card internals
        const cardWidth = columnWidth - 4 * 2;
        const defaultHeight = Math.round(((cardWidth / aspectRatio) * 100) / 100);

        return (
            item && (
                <CellMeasurer key={key} cache={measurerCache} index={index} parent={parent}>
                    {/* @ts-ignore */}
                    <Cell cellStyle={style} role="listitem">
                        {/* @ts-ignore */}
                        {renderItem(item, defaultHeight)}
                    </Cell>
                </CellMeasurer>
            )
        );
    };

    return (
        <Box ref={box} data-testid="masonry grid">
            <AutoSizer onResize={onGridResize} overscanByPixels={overscanByPixels} disableHeight>
                {({ width }) => (
                    <Masonry
                        ref={masonryGridRef}
                        width={width}
                        height={boxHeight}
                        cellCount={items.length}
                        cellRenderer={renderGridItem}
                        cellMeasurerCache={measurerCache}
                        cellPositioner={cellPositioner}
                        onCellsRendered={onCellsRendered}
                        onScroll={handleOnScroll}
                        overscanByPixels={overscanByPixels}
                        role="list"
                    />
                )}
            </AutoSizer>
            {isLoading && (
                <LoadingLayer data-testid="masonry grid loader">
                    <Button isLoading variant="clean" aria-label={translate('pages.loading')} />
                </LoadingLayer>
            )}
        </Box>
    );
}

export default Grid;
